import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from "@angular/material/dialog";

import { DEFAULT_LANGUAGE } from "../../../app/constants";
import { WirelessTicket } from "../../models/wireless-ticket";
import { EmptyState } from "../../subscription-detail/empty-state/empty-state";

/** Cap the dialog to the newest tickets so a long list cannot overflow the screen. */
const MAX_VISIBLE_TICKETS = 10;

const STATUS_LABEL_KEYS: Record<string, string> = {
  Open: "Dialog.Tickets.StatusValue.Open",
  "In Progress": "Dialog.Tickets.StatusValue.In Progress",
  "Waiting for support": "Dialog.Tickets.StatusValue.Waiting for support",
  "Waiting for customer": "Dialog.Tickets.StatusValue.Waiting for customer",
  Waiting: "Dialog.Tickets.StatusValue.Waiting",
  "Waiting with Comment": "Dialog.Tickets.StatusValue.Waiting with Comment",
  "Comment on Ticket": "Dialog.Tickets.StatusValue.Comment on Ticket",
  Resolved: "Dialog.Tickets.StatusValue.Resolved",
  Closed: "Dialog.Tickets.StatusValue.Closed",
};

@Component({
  selector: "dialog-wireless-tickets",
  templateUrl: "dialog-wireless-tickets.html",
  styleUrls: ["dialog-wireless-tickets.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, DatePipe, TranslatePipe, MatDialogClose, EmptyState],
})
export class DialogWirelessTicketsComponent {
  data: { tickets: WirelessTicket[]; scope?: "location" | "organisation" } = inject(MAT_DIALOG_DATA);

  protected dialogRef = inject(MatDialogRef<DialogWirelessTicketsComponent>);
  private translate = inject(TranslateService);

  protected currentLang = computed(() => this.translate.currentLang() ?? DEFAULT_LANGUAGE);
  protected isLocation = computed(() => this.data.scope !== "organisation");
  protected totalCount = this.data.tickets.length;
  protected visibleTickets = this.data.tickets
    .slice(0, MAX_VISIBLE_TICKETS)
    .map((ticket) => ({ ...ticket, statusLabel: this.statusLabel(ticket.status.name) }));
  protected hiddenCount = this.totalCount - this.visibleTickets.length;

  /** Close with a result so the opener decides how to show the organisation-wide tickets. */
  protected viewAll(event: Event) {
    event.preventDefault();
    this.dialogRef.close("viewAll");
  }

  /** Translate a Jira status name, falling back to the raw value for statuses we have no label for. */
  private statusLabel(name: string): string {
    const key = STATUS_LABEL_KEYS[name];
    return key ? this.translate.instant(key) : name;
  }
}
